import clsx from 'clsx';
import { FlaskConical } from 'lucide-react';
import { useMemo, useState } from 'react';

import { formatSig } from './energyCalorimetry';
import { FUELS } from './energyCalorimetrySenior';
import {
  DEFAULT_FUEL_EMISSIONS_SETUP,
  FUEL_EMISSIONS_CHALLENGES,
  FUEL_EMISSIONS_FIELDS,
  FUEL_EMISSIONS_QUESTION,
  type FuelEmissionsSetup,
  calculateFuelEmissions,
  perMJBars,
} from './fuelsEmissionsCalc';
import { ExploreChallenges } from './ExploreChallenges';
import { EMISSIONS_CASES, findFuel } from './fuelsSustainability';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { OptionButton } from './FuelsSustainabilityShared';

const BARS = perMJBars();
const MAX_PER_MJ = Math.max(...BARS.map((bar) => bar.gramsPerMJ));

const PRESETS = EMISSIONS_CASES.filter((entry) => entry.question === 'co2-mass' && entry.fuelKg !== undefined).map(
  (entry) => ({
    id: entry.id,
    setup: { fuelId: entry.fuelId, fuelKg: entry.fuelKg ?? 1 },
    label: `${entry.fuelKg} kg ${findFuel(entry.fuelId)?.name.toLowerCase() ?? entry.fuelId}`,
  }),
);

function Readout({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="rounded-2xl border border-hairline bg-surface p-3">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-slate2">{label}</p>
      <p className="mt-1 text-lg font-extrabold tabular-nums text-ink" data-testid={testId}>
        {value}
      </p>
    </div>
  );
}

/**
 * Emissions Explore: pick a reviewed fuel, type the mass burned and read n(fuel), n(CO₂), m(CO₂)
 * and CO₂ per MJ from one calculation. Invalid input keeps the last valid result.
 */
export function FuelsEmissionsExplore() {
  const [setup, setSetup] = useState<FuelEmissionsSetup>(DEFAULT_FUEL_EMISSIONS_SETUP);
  const result = useMemo(() => calculateFuelEmissions(setup), [setup]);

  return (
    <section
      className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6"
      aria-labelledby="fuels-explore-heading"
    >
      <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
        <FlaskConical className="h-4 w-4" aria-hidden="true" /> Explore
      </p>
      <h3 id="fuels-explore-heading" className="mt-1 text-xl font-extrabold text-ink">
        Burn your own amount of fuel
      </h3>
      <div className="mt-4 grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="grid min-w-0 content-start gap-4">
          <label className="block text-sm font-extrabold text-ink">
            Fuel
            <select
              value={setup.fuelId}
              onChange={(event) => setSetup({ ...setup, fuelId: event.target.value })}
              className="mt-1 h-11 w-full rounded-2xl border border-hairline bg-canvas-pure px-3 text-base font-extrabold text-ink"
            >
              {FUELS.map((fuel) => (
                <option key={fuel.id} value={fuel.id}>
                  {fuel.name} ({fuel.formula}, M = {fuel.molarMass} g mol⁻¹)
                </option>
              ))}
            </select>
          </label>
          <ModelInputPanel
            legend="Mass burned"
            fields={FUEL_EMISSIONS_FIELDS}
            values={{ fuelKg: setup.fuelKg }}
            onChange={(next) => setSetup({ ...setup, fuelKg: next.fuelKg })}
            sliderKey="fuelKg"
          />
          <div className="flex flex-wrap gap-2" role="group" aria-label="Fuel presets">
            {PRESETS.map((preset) => (
              <OptionButton
                key={preset.id}
                pressed={preset.setup.fuelId === setup.fuelId && preset.setup.fuelKg === setup.fuelKg}
                onClick={() => setSetup(preset.setup)}
              >
                {preset.label}
              </OptionButton>
            ))}
          </div>
          <ExploreChallenges question={FUEL_EMISSIONS_QUESTION} challenges={FUEL_EMISSIONS_CHALLENGES} result={result} />
          <div className="grid grid-cols-2 gap-2">
            <Readout label={`n(${result.fuel.formula})`} value={`${formatSig(result.fuelMoles, 3)} mol`} testId="fuels-readout-fuel-mol" />
            <Readout label="n(CO₂)" value={`${formatSig(result.co2Moles, 3)} mol`} testId="fuels-readout-co2-mol" />
            <Readout label="m(CO₂)" value={`${formatSig(result.co2Grams, 3)} g`} testId="fuels-readout-co2-g" />
            <Readout label="m(CO₂)" value={`${formatSig(result.co2Kg, 3)} kg`} testId="fuels-readout-co2-kg" />
          </div>
        </div>
        <WorkedStepsPanel
          title="How the model works it out"
          steps={result.steps}
          stepTestIdPrefix="fuels-step"
          result={`${formatSig(setup.fuelKg, 3)} kg of ${result.fuel.name.toLowerCase()} → ${formatSig(result.co2Kg, 3)} kg CO₂ and ${formatSig(result.energyMJ, 3)} MJ of heat`}
          resultTestId="fuels-explore-result"
          limitNote={result.limitNote}
        />
      </div>
      <div className="mt-5 rounded-3xl border border-hairline bg-surface p-4" aria-label="CO₂ per MJ comparison">
        <p className="text-sm font-extrabold text-ink">CO₂ for each MJ of heat (complete combustion)</p>
        <ul className="mt-3 grid gap-2">
          {BARS.map((bar) => {
            const selected = bar.fuelId === result.fuel.id;
            const value = selected ? result.co2GramsPerMJ : bar.gramsPerMJ;
            return (
              <li key={bar.fuelId} className="grid grid-cols-[96px_minmax(0,1fr)_72px] items-center gap-2 text-xs font-bold">
                <span className={clsx('truncate', selected ? 'font-extrabold text-ink' : 'text-slate2')}>{bar.name}</span>
                <span className="h-3 rounded-full bg-canvas-pure">
                  <span
                    className={clsx('block h-3 rounded-full', selected ? 'bg-brand-coral' : 'bg-brand-sky/40')}
                    style={{ width: `${MAX_PER_MJ > 0 ? (value / MAX_PER_MJ) * 100 : 0}%` }}
                  />
                </span>
                <span
                  className={clsx('text-right tabular-nums', selected ? 'text-ink' : 'text-slate2')}
                  data-testid={selected ? 'fuels-per-mj-selected' : undefined}
                >
                  {formatSig(value, 3)} g
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
